import throttle from 'lodash/throttle';
import isEqual from 'lodash/isEqual';
import type { RefObject, Dispatch, SetStateAction } from 'react';
import { useState, useEffect, useRef, useMemo } from 'react';
import { DeckGL, type DeckGLRef } from '@deck.gl/react';
import type {
  PickingInfo,
  ViewStateChangeParameters,
  LayersList,
} from '@deck.gl/core';
import { WebMercatorViewport } from '@deck.gl/core';
import type { PopupOptions } from 'react-map-gl/mapbox';
import {
  GeolocateControl,
  Map as ReactGlMap,
  Marker,
  Popup,
} from 'react-map-gl/mapbox';
import { useAppDispatch, useAppSelector } from '@/app/hooks';
import { useUserClientContext } from '@/app/clientContext';
import { useDatasets } from '@/hooks/useDatasets';
import { LoadingOverlay } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useClientTerminology } from '@/views/list/NetworkTerms';

import { getHighlightLayer, getLayers } from './map/layers';
import type {
  LayerConfigMap,
  RiskTypeConfig,
  TreesAggregateData,
} from './map/layers/types';
import { LayerType, CLICKABLE_LAYERS } from './map/layers/types';
import {
  exposeDeckGLTestHelpers,
  removeDeckGLTestHelpers,
} from './map/deckglE2ETestHelpers';

import type { SelectedItem } from '@/features/analysisMapSlice';
import {
  clearItem,
  selectItem,
  setActiveGroupName,
  updateMapNetworkGroupId,
  updateViewState,
  updateMapState,
} from '@/features/analysisMapSlice';
import {
  useFetchFieldSpeciesQuery,
  useFetchFieldsQuery,
  groupBySpeciesCategory,
} from '@/features/fieldSlice';
import { MapPopover } from './map/popover/MapPopover';
import { HoverMarker } from './map/HoverMarker';
import hoverMarkerClasses from './map/HoverMarker.module.css';
import { useSmallScreen } from '@/hooks/useWindowDimension';
import { useMobileInspectMode } from '@/hooks/useMobileInspectMode';
import { generatePath, matchPath, useLocation } from 'react-router-dom';
import { useCustomMapStyle } from './map/layers/baseLayer';
import { shouldSkipGroupSummary } from './map/layers/networkGroupLayer';
import { Icon, LogoType } from '@/components';
import { LoadWidget } from '@/views/map/MapLoadIndicator/MapLoadIndicator';
import { useTouch } from '@/hooks/useTouch';
import { useHover } from '@/features/hoverContext';
import {
  OVERVIEW_ZOOM_THRESHOLD,
  LISTS_DETAIL_PATH,
  LISTS_SPAN_PATH,
} from '@/app/constants';
import { useDeliveryIdentifier } from '@/hooks/useDeliveryIdentifier';
import { useGeolocateCallbacks } from './map/useGeolocateCallbacks';
import { useMapGroupHover } from './map/useMapGroupHover';
import {
  useGeolocateControl,
  GeolocateStatus,
} from '@/features/geolocateContext';
import { InspectTooltip } from './map/inspect/InspectTooltip';
import TagManager from 'react-gtm-module';
import { GoogleEvents } from '@/features/googleEvents';
import { ContextMenu } from './map/ContextMenu';
import useLongPress from '@/hooks/useLongPress';
import CustomMapView from './map/CustomMapView';
import { updateWorklogStatus } from '@/features/worklogSlice';
import { ViewModeOptions, type SatelliteImageMetadata } from '@/features/types';
import { GroupAnalysisType } from '@/generated/types/navigator/types.gen';
import { isiOS, isInspectLayerActive } from './map/utils';
import {
  DESKTOP_VIEWBOX_PADDING,
  MOBILE_VIEWBOX_PADDING,
  updateViewport,
  coordsToQuadkey,
  toSelectedItem,
} from './map/deckglUtils';
import { useFilterGroupSummaryAnalysisQuery } from '@/features/analysisApi';
import { useFuelHazardStatisticsQuery } from '@/features/statisticsSlice';
import type { Coordinates, LngLatBase } from './map/types';
import {
  useHasFuelsData,
  useHasGroupRiskSummary,
} from '@/hooks/useWildfireMode';
import { useHasFuelsWithoutWildfirePriority } from '@/hooks/useHasFuelsWithoutWildfirePriority';
import { WKTBBoxToArray } from '@/helpers/stringConversion';
import { MetricEmitter } from '@/helpers/metrics';

// Utilized (and installed) by Deck.gl
import type { MjolnirGestureEvent, MjolnirPointerEvent } from 'mjolnir.js';
import type { Feature } from 'geojson';
import type { RedFlagWarning } from '@/features/wildfireSlice';
import { useThrottledCallback } from '@mantine/hooks';
import { useCurrentRootPath } from './useUrlSync';
import { useNavigateWithSearch } from '@/hooks/useNavigateWithSearch';
import { useSpreadPotentialViewedAnalytics } from '@/hooks/useSpreadPotentialViewedAnalytics';
import debounce from 'lodash/debounce';
import type { PickMultipleObjectsMethod } from '@/hooks/useSatelliteImageMetadata';
import { isInProjectUrl } from '@/helpers/url';
import { captureException } from '@sentry/core';
import { useAppFlags } from '@/hooks/useAppFlags';

const KEYBOARD_INTERACTION = {
  zoomSpeed: 25,
  moveSpeed: 1000,
  rotateSpeedX: 75,
  rotateSpeedY: 75,
};

// Workaround for: https://github.com/visgl/deck.gl/issues/7956
const mapView = new CustomMapView({
  controller: {
    keyboard: KEYBOARD_INTERACTION,
    inertia: !isiOS ? true : false,
    dragRotate: false,
  },
});

interface MapViewProps {
  width: number;
  height: number;
  mapboxToken: string;
  deckGl: RefObject<DeckGLRef<CustomMapView> | null>;
  selectedItem: SelectedItem;
  layerConfig: LayerConfigMap;
  selectedTreeCover: string;
  clickInfo: { x: number; y: number };
  anchor: PopupOptions['anchor'];
  popupOffset?: number;
  popupHeight: number;
  setPopupHeight: (height: number) => void;
  treesData: Feature[];
  setTreesData: React.Dispatch<React.SetStateAction<Feature[]>>;
  treesAggregateData: TreesAggregateData[];
  setTreesAggregateData: Dispatch<SetStateAction<TreesAggregateData[]>>;
  setSatelliteMetadata: Dispatch<SetStateAction<Feature[]>>;
  satelliteMetadata: Feature[];
  satelliteImageMetadata: SatelliteImageMetadata | undefined;
  pickAndSetSatelliteMetadata: (
    coordinates: [number, number],
    satelliteMetadata: Feature[],
    pickMultipleObjects: PickMultipleObjectsMethod
  ) => void;
  contextMenu: Coordinates | undefined;
  setContextMenu: (c: Coordinates | undefined) => void;
  pageDimensions: { x: number; y: number } | undefined;
  setPageDimensions: (pageDimensions: { x: number; y: number }) => void;
  locationPin: LngLatBase | undefined;
  setLocationPin: (coords: LngLatBase | undefined) => void;
  redFlagWarnings: RedFlagWarning[] | undefined;
  initialViewportReady: boolean;
}

export type HoverInfo = {
  x?: number;
  y?: number;
  contents?: string;
};

export const MapView = ({
  mapboxToken,
  selectedItem,
  height,
  width,
  layerConfig,
  selectedTreeCover,
  clickInfo,
  anchor,
  popupOffset,
  popupHeight,
  setPopupHeight,
  treesData,
  setTreesData,
  treesAggregateData,
  setTreesAggregateData,
  setSatelliteMetadata,
  satelliteMetadata,
  satelliteImageMetadata,
  pickAndSetSatelliteMetadata,
  contextMenu,
  setContextMenu,
  pageDimensions,
  setPageDimensions,
  locationPin,
  setLocationPin,
  redFlagWarnings,
  initialViewportReady,
  ...props
}: MapViewProps) => {
  const { apiIdentity } = useUserClientContext();
  const {
    datasets,
    isSuccess: isDatasetsLoaded,
    isError: didDatasetsFail,
  } = useDatasets();
  const intl = useClientTerminology();
  const {
    flagLoadingWheelDelay = 2000,
    flagUsePostForMvtGroupAnalysis = false,
    flagNetworkOutlineWhenFiltering = false,
  } = useAppFlags();
  const hasFuelsData = useHasFuelsData();
  const hasGroupRiskSummary = useHasGroupRiskSummary();
  const hasFuelsWithoutWildfirePriority = useHasFuelsWithoutWildfirePriority();
  useSpreadPotentialViewedAnalytics();

  // Surface a toast when the datasets request fails. The loading overlay stays
  // up (it only clears on a successful load), so the notification is the only
  // signal the user gets that something went wrong.
  useEffect(() => {
    if (didDatasetsFail) {
      notifications.show({
        color: 'red',
        title: intl.formatMessage({ id: 'action.error' }),
        message: intl.formatMessage({ id: 'action.genericError' }),
      });
    }
    // Fire once per failure transition; `intl` is intentionally omitted to
    // avoid re-showing the toast on unrelated re-renders.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [didDatasetsFail]);

  // Strip the fuel_hazard dataset when the wildfire mode doesn't expose fuels so the deck.gl
  // layer list reactively drops the mosaic if the flag flips off mid-session. Without this,
  // the datasets stay frozen until the next page load and the layer would persist.
  const gatedDatasets = useMemo(() => {
    if (hasFuelsData) return datasets;
    return datasets.filter(
      (d) => d.layerType !== LayerType[LayerType.fuel_hazard]
    );
  }, [datasets, hasFuelsData]);

  const location = useLocation();
  const dispatch = useAppDispatch();
  const query = useAppSelector((state) => state.filterQuery);
  const [isOverview, setIsOverview] = useState(true);
  const {
    isInspectMode,
    selectedAnalysis,
    selectedNetworkGroup,
    encroachmentThresholds,
    priorityThresholds,
    wildfirePriorityThresholds,
    criticalTreesThresholds,
    clearThresholds,
    spanNetworkGroup,
    activeGroupName,
    mapNetworkGroupId,
    viewState,
    mapState,
    currentViewMode,
    mvtCacheCounter,
  } = useAppSelector((s) => s.analysisMap);
  const [marker, setMarker] = useState({
    lat: 39,
    lng: -105,
  });
  const { data: fields } = useFetchFieldsQuery();
  const { data: species } = useFetchFieldSpeciesQuery(undefined, {
    skip: !fields || !fields.map((i) => i.field).includes('species'),
  });
  const devMode = useAppSelector((state) => state.devMode);

  const { updatedSpecies } = useMemo(
    () => groupBySpeciesCategory(species),
    [species]
  );

  const [hoverInfo, setHoverInfo] = useState<HoverInfo>({});
  const INITIAL_COORDINATES: [number, number] = [-200, -200];
  const [tooltipCoordinates, setTooltipCoordinates] =
    useState<[number, number]>(INITIAL_COORDINATES);
  const navigateWithSearch = useNavigateWithSearch();
  const [layers, setLayers] = useState<PickingInfo[]>();
  const isTouchDevice = useTouch();
  const isSmallScreen = useSmallScreen();
  const {
    status: geolocateStatus,
    setStatus: setGeolocateStatus,
    setGeolocateControlRef,
  } = useGeolocateControl();
  const isMobileInspectMode = useMobileInspectMode();
  const isDesktopInspectMode = !isSmallScreen && isInspectMode;
  const { statusChanged } = useAppSelector((state) => state.worklogMap);
  const { showHazardTrees, showDecliningTrees, showStrikeTrees } = layerConfig[
    LayerType.trees
  ] as RiskTypeConfig;
  const { data: groupSummary, isLoading: groupSummaryIsLoading } =
    useFilterGroupSummaryAnalysisQuery(
      {
        group_id: selectedNetworkGroup?.id,
        analysis_type: selectedAnalysis,
        query,
      },
      {
        skip: shouldSkipGroupSummary(
          selectedNetworkGroup?.id,
          spanNetworkGroup?.id,
          activeGroupName
        ),
      }
    );
  const { data: fuelHazardStats } = useFuelHazardStatisticsQuery(undefined, {
    skip: !hasFuelsData,
  });

  const [actualZoom, setActualZoom] = useState<number>(viewState.zoom);
  const startTime = useRef(Date.now());
  const dataLoadTime = useRef<number | null>(null);
  const firstRenderComplete = useRef(false);
  const loginState = useAppSelector((state) => state.login);
  const renderStabilityTimer = useRef<number | null>(null);
  const metrics = useMemo(() => new MetricEmitter(loginState), [loginState]);

  const { hoveredItem, hoverLocation } = useHover();
  const viewStateRef = useRef(viewState);

  const { onGeolocate, onGeolocateError } = useGeolocateCallbacks();
  const deliveryIdentifier = useDeliveryIdentifier();

  useEffect(() => {
    if (!firstRenderComplete.current && groupSummary) {
      dataLoadTime.current = Date.now();
      metrics.emit(
        'map_data_load_time',
        (Date.now() - startTime.current) / 1000.0
      );
    }
  }, [groupSummary, metrics]);

  const onAfterRender = () => {
    const loadTime = dataLoadTime.current;
    if (!firstRenderComplete.current && loadTime) {
      if (renderStabilityTimer.current) {
        clearTimeout(renderStabilityTimer.current);
      }
      renderStabilityTimer.current = window.setTimeout(() => {
        firstRenderComplete.current = true;
        const loadToRenderTime = Date.now() - loadTime;
        const totalRenderTime = Date.now() - startTime.current;
        metrics.emit('login_to_map_render_time', totalRenderTime / 1000.0);
        metrics.emit('map_data_load_to_render_time', loadToRenderTime / 1000.0);
      }, 2000);
    }
  };

  useEffect(() => {
    if (viewState) {
      const isOverviewZoomLevel = viewState.zoom <= OVERVIEW_ZOOM_THRESHOLD;
      if (isOverview !== isOverviewZoomLevel) {
        setIsOverview(isOverviewZoomLevel);
      }
      setMarker({
        lng: viewState.longitude,
        lat: viewState.latitude,
      });
      setActualZoom(viewState.zoom);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewState]);

  const listId = currentViewMode.modeDetails?.id ?? null;

  // Quantize zoom to nearest 0.5 to reduce unnecessary re-renders
  const quantizedZoom = useMemo(() => {
    return Math.round(actualZoom * 2) / 2;
  }, [actualZoom]);

  const deckLayers: LayersList = useMemo(() => {
    const layers: LayersList = getLayers(
      gatedDatasets,
      isOverview,
      apiIdentity,
      query,
      listId,
      layerConfig,
      isInspectMode,
      statusChanged,
      selectedNetworkGroup?.id ?? null,
      mapNetworkGroupId,
      updatedSpecies,
      setHoverInfo,
      selectedAnalysis,
      treesData,
      setTreesData,
      treesAggregateData,
      setTreesAggregateData,
      setSatelliteMetadata,
      satelliteMetadata,
      isSmallScreen,
      showHazardTrees,
      showDecliningTrees,
      showStrikeTrees,
      quantizedZoom,
      encroachmentThresholds,
      priorityThresholds,
      wildfirePriorityThresholds,
      criticalTreesThresholds,
      clearThresholds,
      spanNetworkGroup?.id,
      activeGroupName,
      (!groupSummaryIsLoading && groupSummary) || new Map(),
      redFlagWarnings,
      mvtCacheCounter,
      hoveredItem?.groupName,
      fuelHazardStats?.max,
      hasGroupRiskSummary,
      hasFuelsWithoutWildfirePriority,
      flagUsePostForMvtGroupAnalysis,
      flagNetworkOutlineWhenFiltering
    );
    layers.push(getHighlightLayer(selectedItem?.object, isMobileInspectMode));
    return layers;
  }, [
    gatedDatasets,
    isOverview,
    apiIdentity,
    query,
    listId,
    layerConfig,
    isInspectMode,
    statusChanged,
    selectedNetworkGroup?.id,
    mapNetworkGroupId,
    updatedSpecies,
    selectedAnalysis,
    treesData,
    setTreesData,
    treesAggregateData,
    setTreesAggregateData,
    setSatelliteMetadata,
    satelliteMetadata,
    isSmallScreen,
    showHazardTrees,
    showDecliningTrees,
    showStrikeTrees,
    quantizedZoom,
    encroachmentThresholds,
    priorityThresholds,
    wildfirePriorityThresholds,
    criticalTreesThresholds,
    clearThresholds,
    spanNetworkGroup?.id,
    activeGroupName,
    groupSummaryIsLoading,
    groupSummary,
    redFlagWarnings,
    selectedItem?.object,
    isMobileInspectMode,
    mvtCacheCounter,
    hoveredItem?.groupName,
    fuelHazardStats?.max,
    hasGroupRiskSummary,
    hasFuelsWithoutWildfirePriority,
    flagUsePostForMvtGroupAnalysis,
    flagNetworkOutlineWhenFiltering,
  ]);

  const pickLayers = useMemo(() => {
    return throttle((coordinates: [number, number]) => {
      const deck = props.deckGl.current;
      if (!deck) {
        return;
      }
      const result = deck.pickMultipleObjects({
        x: coordinates[0],
        y: coordinates[1],
        layerIds: [
          LayerType[LayerType.tree_type],
          LayerType[LayerType.distance_bucket],
          LayerType[LayerType.overhang],
          LayerType[LayerType.worktype_analysis],
          LayerType[LayerType.tree_species],
          LayerType[LayerType.scan],
        ],
      });
      setLayers(result);
    }, 100);
  }, [props.deckGl]);

  // Expose deck.gl test helpers for E2E testing
  useEffect(() => {
    exposeDeckGLTestHelpers(props.deckGl);
    return () => {
      removeDeckGLTestHelpers();
    };
  }, [props.deckGl]);
  // Refs for use by onViewStateChange
  const hasDoneInitialDispatch = useRef(false);
  const lastInsideZoomThreshold = useRef<boolean>(false);

  const captureMapState = useMemo(
    // This must be debounced -- otherwise we swarm the reducers
    // with constant updates.
    () =>
      debounce(
        (state) => {
          dispatch(updateMapState(state));
        },
        1000,
        { leading: true }
      ),
    [dispatch]
  );

  const onViewStateChange = (state: ViewStateChangeParameters) => {
    // If user manually pans/zooms while TRACKING, keep showing location but stop auto-panning
    if (
      (state.interactionState?.isPanning ||
        state.interactionState?.isZooming) &&
      geolocateStatus === GeolocateStatus.TRACKING
    ) {
      // Transition to VISIBLE state - keeps location visible but stops auto-pan
      setGeolocateStatus(GeolocateStatus.VISIBLE);
    }

    // This is about centering and zooming the customer's map
    if (!hasDoneInitialDispatch.current) {
      dispatch(updateViewState(state.viewState));
      hasDoneInitialDispatch.current = true;
    } else {
      captureMapState(state.viewState);
    }

    setMarker({
      lng: state.viewState.longitude,
      lat: state.viewState.latitude,
    });
    if (!isSmallScreen) {
      setContextMenu(undefined);
    }
    if (
      isTouchDevice &&
      layerConfig[LayerType.base].visible &&
      props.deckGl.current
    ) {
      try {
        pickAndSetSatelliteMetadata(
          [state.viewState.width / 2, state.viewState.height / 2],
          satelliteMetadata,
          props.deckGl.current.pickMultipleObjects.bind(props.deckGl.current)
        );
      } catch (error) {
        captureException(error);
      }
    }

    const zoom = state.viewState.zoom;
    const isOverviewZoomLevel = zoom < OVERVIEW_ZOOM_THRESHOLD;
    if (isOverview !== isOverviewZoomLevel) {
      setIsOverview(isOverviewZoomLevel);
    }
    if (
      isMobileInspectMode &&
      viewState !== state.viewState &&
      (state.interactionState.isZooming ||
        state.interactionState.isPanning ||
        state.interactionState.isDragging)
    ) {
      pickLayers([state.viewState.width / 2, state.viewState.height / 2]);
    }
    // To fix CA-537, we must update redux viewState as we cross the zoom threshold
    // TODO: Remove this as part of PP-195 refactor
    const insideZoomThreshold = state.viewState.zoom > OVERVIEW_ZOOM_THRESHOLD;
    if (lastInsideZoomThreshold.current !== insideZoomThreshold) {
      dispatch(updateViewState(state.viewState));
    }
    lastInsideZoomThreshold.current = insideZoomThreshold;

    setActualZoom(state.viewState.zoom);

    viewStateRef.current = state.viewState;
  };

  const popover = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (popover.current) {
      setPopupHeight(popover.current.clientHeight);
    }
  }, [popupHeight, setPopupHeight, popover.current?.clientHeight]);

  const mapStyle = useCustomMapStyle();
  const rootPath = useCurrentRootPath();
  const onLongPress = (evt: React.TouchEvent) => {
    if (
      evt.changedTouches &&
      evt.changedTouches.length === 1 &&
      !isMobileInspectMode
    ) {
      const { pageX, pageY } = evt.changedTouches[0];
      const BUFFER = 72;
      setPageDimensions({
        x: !isSmallScreen && isTouchDevice ? pageX - BUFFER : pageX,
        y: pageY,
      });
      if (selectedItem) {
        dispatch(clearItem());
        dispatch(updateWorklogStatus(false));
        navigateWithSearch(rootPath);
      }
    }
  };

  const defaultOptions = {
    shouldPreventDefault: true,
    delay: 300,
  };

  const longPressEvent = useLongPress(onLongPress, defaultOptions);

  useEffect(() => {
    if (pageDimensions) {
      const viewport = new WebMercatorViewport(viewState);
      const worldCoordinates = viewport.unproject([
        pageDimensions.x,
        pageDimensions.y,
      ]);
      setContextMenu({
        lng: worldCoordinates[0],
        lat: worldCoordinates[1],
        x: pageDimensions.x,
        y: pageDimensions.y,
      });
      setLocationPin({
        lng: worldCoordinates[0],
        lat: worldCoordinates[1],
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageDimensions]);

  useEffect(() => {
    if (contextMenu) {
      TagManager.dataLayer({
        dataLayer: {
          event: GoogleEvents.openContextMenu,
        },
      });
    }
  }, [contextMenu]);

  // Controls the network group displayed on the map
  useEffect(() => {
    if (currentViewMode.mode !== ViewModeOptions.LEGACY) {
      return;
    }
    if (
      viewState.zoom >= OVERVIEW_ZOOM_THRESHOLD ||
      activeGroupName ||
      selectedNetworkGroup?.id === spanNetworkGroup?.id
    ) {
      dispatch(updateMapNetworkGroupId(spanNetworkGroup?.id ?? null));
    } else {
      dispatch(updateMapNetworkGroupId(selectedNetworkGroup?.id ?? null));
    }
  }, [
    viewState.zoom,
    activeGroupName,
    selectedNetworkGroup?.id,
    spanNetworkGroup?.id,
    dispatch,
    currentViewMode.mode,
  ]);

  const printQuadkey = useThrottledCallback((d) => {
    if (d?.coordinate) {
      // eslint-disable-next-line no-console
      console.log(
        'quadkey: ',
        coordsToQuadkey(d?.coordinate[1], d?.coordinate[0])
      );
    }
  }, 2000);

  const isSpanView =
    selectedNetworkGroup?.id === spanNetworkGroup?.id || !!activeGroupName;

  const handleMapGroupHover = useMapGroupHover({ viewStateRef, isSpanView });

  const onMapHover = (d: PickingInfo, e: MjolnirPointerEvent) => {
    if (devMode.enabled) {
      printQuadkey(d);
    }
    if (
      e.target.closest('.mapboxgl-popup') ||
      e.target.id === 'deckgl-wrapper'
    ) {
      return;
    }
    if (isTouchDevice) return;
    if (d.coordinate && d.layer && !isOverview) {
      if (isInspectLayerActive(layerConfig)) {
        setTooltipCoordinates([d.x, d.y]);
        pickLayers([d.x, d.y]);
      } else {
        if (!isEqual(INITIAL_COORDINATES, tooltipCoordinates)) {
          setTooltipCoordinates(INITIAL_COORDINATES);
        }
        pickLayers(INITIAL_COORDINATES);
      }
    }
    if (
      d.coordinate &&
      d.layer &&
      layerConfig[LayerType.base].visible &&
      !isOverview &&
      props.deckGl.current
    ) {
      pickAndSetSatelliteMetadata(
        [d.x, d.y],
        satelliteMetadata,
        props.deckGl.current.pickMultipleObjects.bind(props.deckGl.current)
      );
    }

    handleMapGroupHover(d);
  };

  function handleImprovedZoomSpanClick(d: PickingInfo) {
    const listDetailsMatch = matchPath(LISTS_DETAIL_PATH, location.pathname);
    const listsSpanMatch = matchPath(LISTS_SPAN_PATH, location.pathname);
    let currentListId: string;

    if (listDetailsMatch?.params.id) {
      currentListId = listDetailsMatch.params.id;
    } else if (listsSpanMatch?.params.id) {
      currentListId = listsSpanMatch.params.id;
    } else {
      navigateWithSearch(
        `/group/${encodeURIComponent(d.object.properties.group)}`
      );
      return;
    }

    const level2 = d.object.properties.level2;
    const level3 = d.object.properties.group;
    const url = generatePath(LISTS_SPAN_PATH, {
      id: currentListId,
      spanId: `${btoa(level2)}~${btoa(level3)}`,
    });
    navigateWithSearch(url);
  }

  const onMapClick = (d: PickingInfo, e: MjolnirGestureEvent) => {
    if (
      e.target.closest('.mapboxgl-popup') ||
      e.target.closest('.mantine-Menu-item')
    )
      return;
    if (locationPin) {
      setLocationPin(undefined);
    }
    if (e.rightButton) {
      if (d.coordinate) {
        const [lng, lat] = d.coordinate;
        setContextMenu({ lng, lat, x: d.x, y: d.y });
        setLocationPin({ lng, lat });
      }
      if (selectedItem) {
        dispatch(clearItem());
        dispatch(updateWorklogStatus(false));
        navigateWithSearch(rootPath);
      }
      return;
    } else {
      setContextMenu(undefined);
    }

    if (isMobileInspectMode) {
      return;
    }

    if (
      d.layer &&
      d.layer.id === LayerType[LayerType.red_flag_warning] &&
      props.deckGl.current
    ) {
      const pickInfo = props.deckGl.current.pickObject({
        x: d.x,
        y: d.y,
        layerIds: [LayerType[LayerType.red_flag_warning]],
      });
      if (pickInfo) {
        dispatch(selectItem(toSelectedItem(pickInfo)));
        navigateWithSearch(rootPath);
      }
      return;
    }
    if (
      d.layer &&
      CLICKABLE_LAYERS.includes(d.layer.id) &&
      props.deckGl.current
    ) {
      const pickInfo = props.deckGl.current.pickObject({
        x: d.x,
        y: d.y,
        layerIds: [LayerType[LayerType.scan]],
      });
      dispatch(pickInfo ? selectItem(toSelectedItem(pickInfo)) : clearItem());
      navigateWithSearch(rootPath);
      return;
    }

    if (d.layer && d.layer.id === LayerType[LayerType.scan] && props.deckGl) {
      if (isInProjectUrl(currentViewMode)) {
        TagManager.dataLayer({
          dataLayer: {
            event: GoogleEvents.projects.engagement.spanClickFromMap,
          },
        });
      }

      if (mapNetworkGroupId === spanNetworkGroup?.id) {
        handleImprovedZoomSpanClick(d);
        return;
      }
      if (
        selectedNetworkGroup?.id !== spanNetworkGroup?.id &&
        !activeGroupName
      ) {
        const newSelectedGroupName = d.object.properties.group;
        if (!newSelectedGroupName) {
          return;
        }
        dispatch(setActiveGroupName(newSelectedGroupName));

        if (d.object.properties.bbox) {
          dispatch(
            updateViewState(
              updateViewport(
                viewState,
                [...WKTBBoxToArray(d.object.properties.bbox)],
                width, // current width
                DESKTOP_VIEWBOX_PADDING,
                MOBILE_VIEWBOX_PADDING
              )
            )
          );
        }
        return;
      }
    }
    if (!d.layer) {
      // Clicked on empty map background — close any open detail panel
      dispatch(clearItem());
      dispatch(updateWorklogStatus(false));
      navigateWithSearch(rootPath);
      return;
    }
    navigateWithSearch(rootPath);
    dispatch(selectItem(toSelectedItem(d)));
  };

  const popoverComponent = useMemo(() => {
    if (selectedItem) {
      return <MapPopover target={selectedItem} popover={popover} />;
    }
  }, [selectedItem]);

  const { bearing, pitch, bbox, transitionInterpolator, ...deckGLViewState } =
    viewState;

  const deckId = deliveryIdentifier ?? '';

  return (
    <>
      <div
        id="map-wrapper"
        data-test-zoom={mapState.zoom}
        data-test-longitude={mapState.longitude}
        data-test-latitude={mapState.latitude}
        data-test-initial-viewport-ready={initialViewportReady}
        onContextMenu={(evt) => {
          evt.preventDefault();
        }}
        {...longPressEvent}
        style={{ width: '100%', height: '100%' }}
      >
        <LoadingOverlay
          visible={!isDatasetsLoaded}
          overlayProps={{ backgroundOpacity: 1 }}
        />
        <DeckGL
          key={`map-deck-${deckId}`}
          // eslint-disable-next-line no-restricted-syntax
          style={{ backgroundColor: '#1e1e1e' }}
          ref={props.deckGl}
          width={width}
          height={height}
          onHover={onMapHover}
          onClick={onMapClick}
          initialViewState={{ ...deckGLViewState }}
          eventRecognizerOptions={
            isSmallScreen
              ? {
                  pan: { threshold: 10 },
                  click: { threshold: 5 },
                }
              : {}
          }
          getCursor={(props: { isDragging: boolean; isHovering: boolean }) =>
            props.isDragging
              ? 'grabbing'
              : props.isHovering
                ? 'pointer'
                : 'grab'
          }
          onViewStateChange={onViewStateChange}
          views={mapView}
          widgets={[
            new LoadWidget({
              delay: flagLoadingWheelDelay,
              devModeEnabled: devMode.enabled,
            }),
          ]}
          layers={deckLayers}
          pickingRadius={10}
          onAfterRender={onAfterRender}
        >
          <ReactGlMap
            mapStyle={mapStyle}
            mapboxAccessToken={mapboxToken}
            attributionControl={false}
            logoPosition="bottom-right"
            id="mapbox-map"
          >
            {/*
            This is the "dot" that indicates the user's position.
            It also handles interfacing with the browser to get location data,
            which re receive via the onGeolocate and onGeolocateError callbacks.
            It does _not_ update the map's view state automatically; we handle that.
             */}
            <GeolocateControl
              ref={(ref) => {
                if (ref) {
                  setGeolocateControlRef(ref);
                }
              }}
              positionOptions={{ timeout: 10000, enableHighAccuracy: true }}
              trackUserLocation={true}
              showUserHeading={true}
              showAccuracyCircle={true}
              showUserLocation={true}
              onGeolocate={onGeolocate}
              onError={onGeolocateError}
            />

            <div>
              {isMobileInspectMode && (
                <Marker latitude={marker.lat} longitude={marker.lng}>
                  <Icon
                    name="Cross"
                    color="var(--mantine-color-white)"
                    size={24}
                  />
                </Marker>
              )}
              {locationPin && (
                <Marker
                  latitude={locationPin.lat}
                  longitude={locationPin.lng}
                  // This gets rids of all the marker events and passes
                  // events through to the underlying map.
                  style={{ pointerEvents: 'none' }}
                  anchor="bottom"
                >
                  <div className={hoverMarkerClasses.hoverPin}>
                    <Icon
                      name="Pin"
                      color="var(--mantine-color-white)"
                      size={72}
                    />
                  </div>
                </Marker>
              )}
              {selectedItem?.layer?.id === LayerType[LayerType.scan] &&
                clickInfo.x !== 0 &&
                clickInfo.y !== 0 && (
                  <Marker
                    latitude={clickInfo.y}
                    longitude={clickInfo.x}
                    style={{ pointerEvents: 'none' }}
                    anchor="bottom"
                  >
                    <div className={hoverMarkerClasses.hoverPin}>
                      <Icon
                        name="Pin"
                        color="var(--mantine-color-white)"
                        size={72}
                      />
                    </div>
                  </Marker>
                )}
              {hoverLocation && !isSmallScreen && (
                <HoverMarker
                  hoverLocation={hoverLocation}
                  isOverview={isOverview}
                  isSpanView={isSpanView}
                  hoveredItem={hoveredItem}
                />
              )}
              {!isMobileInspectMode &&
                !isSmallScreen &&
                selectedItem?.layer?.id !== LayerType[LayerType.scan] && (
                  <div>
                    <Popup
                      className="popup-map"
                      longitude={clickInfo.x}
                      latitude={clickInfo.y}
                      closeButton={false}
                      anchor={anchor}
                      offset={popupOffset}
                      closeOnClick={false}
                      maxWidth="400px"
                    >
                      {popoverComponent}
                    </Popup>
                  </div>
                )}
            </div>

            {!isMobileInspectMode && (
              <div>
                {isSmallScreen && !satelliteImageMetadata && (
                  <div
                    id="mobile-logo"
                    style={{
                      position: 'absolute',
                      bottom: '64px',
                      display: 'block',
                    }}
                  >
                    <LogoType theme="light" withOutline />
                  </div>
                )}
              </div>
            )}
          </ReactGlMap>
        </DeckGL>
      </div>
      {(isMobileInspectMode || isDesktopInspectMode) &&
        selectedAnalysis !== GroupAnalysisType.FALL_IN && (
          <InspectTooltip
            isOverview={isOverview}
            layerConfig={layerConfig}
            tooltipCoordinates={tooltipCoordinates}
            hoverInfo={hoverInfo}
            layers={layers}
            setHoverInfo={setHoverInfo}
          />
        )}
      {contextMenu && <ContextMenu coordinates={contextMenu} />}
    </>
  );
};
